import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import jsPDF from "jspdf";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ChevronLeft, Download, RefreshCw, Loader2, Shield, FileText, History } from "lucide-react";
import { SecurityScheduleSettingsPanel } from "@/components/admin/SecurityScheduleSettingsPanel";
import { SecurityScanJobPanel } from "@/components/admin/SecurityScanJobPanel";
import { SecurityScanComparison } from "@/components/admin/SecurityScanComparison";
import { SecurityRemediationPanel } from "@/components/admin/SecurityRemediationPanel";


type Finding = {
  id: string;
  internal_id: string;
  scanner_name: string;
  level: string | null;
  name: string | null;
  description: string | null;
  status: "open" | "ignored" | "fixed";
  ignore_reason: string | null;
  decided_by: string | null;
  decided_at: string | null;
  last_seen_at: string;
  created_at: string;
};

type Snapshot = {
  id: string;
  source: string;
  git_sha: string | null;
  finding_count: number;
  new_count: number;
  created_at: string;
  findings?: Array<{ internal_id: string; scanner_name: string; level?: string | null; name?: string | null; description?: string | null }>;
  counts_by_id?: Record<string, number>;
};

type Rescan = {
  id: string;
  source: string;
  reason: string | null;
  status: string;
  latency_ms: number | null;
  created_at: string;
};

type DeliveryFailure = {
  id: string;
  snapshot_id: string | null;
  channel: "slack" | "email";
  target: string | null;
  attempts: number;
  last_error: string | null;
  last_status_code: number | null;
  new_count: number | null;
  finding_count: number | null;
  resolved_at: string | null;
  created_at: string;
  last_attempt_at: string | null;
  next_attempt_at: string | null;
};

type CiSecret = {
  key: string;
  hint: string | null;
  rotated_at: string;
  rotated_by: string | null;
};

type AuditRow = {
  id: string;
  user_id: string;
  action: string;
  target_table: string | null;
  target_id: string | null;
  details: Record<string, unknown> | null;
  created_at: string;
};

type AlertSettings = {
  slack_webhook_url: string | null;
  notify_emails: string[];
  notify_on_new_findings: boolean;
  notify_web_push: boolean;
  enabled: boolean;
};

const STATUS_FILTERS = ["all", "open", "ignored", "fixed"] as const;
type StatusFilter = (typeof STATUS_FILTERS)[number];

export default function AdminSecurityFindings() {
  const [findings, setFindings] = useState<Finding[]>([]);
  const [snapshots, setSnapshots] = useState<Snapshot[]>([]);
  const [rescans, setRescans] = useState<Rescan[]>([]);
  const [ciSecret, setCiSecret] = useState<CiSecret | null>(null);
  const [audit, setAudit] = useState<AuditRow[]>([]);
  const [alertCfg, setAlertCfg] = useState<AlertSettings>({
    slack_webhook_url: "",
    notify_emails: [],
    notify_on_new_findings: true,
    notify_web_push: false,
    enabled: true,
  });
  const [savingCfg, setSavingCfg] = useState(false);
  const [rotatedPlaintext, setRotatedPlaintext] = useState<string | null>(null);
  const [rotating, setRotating] = useState(false);
  const [filter, setFilter] = useState<StatusFilter>("open");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState<"csv" | "md" | "json" | null>(null);
  const [triggering, setTriggering] = useState(false);
  const [failures, setFailures] = useState<DeliveryFailure[]>([]);
  const [tlStatus, setTlStatus] = useState<"all" | "pass" | "block">("all");
  const [tlKind, setTlKind] = useState<"all" | "manual" | "ci" | "migration">("all");
  const [tlFrom, setTlFrom] = useState("");
  const [tlTo, setTlTo] = useState("");
  const [tlPage, setTlPage] = useState(0);
  const [diffSnap, setDiffSnap] = useState<Snapshot | null>(null);
  const [productName, setProductName] = useState<string>(
    () => (typeof window !== "undefined" && localStorage.getItem("pdf-report-product-name")) || "Trends Discovery",
  );
  const [reportFooter, setReportFooter] = useState<string>(
    () => (typeof window !== "undefined" && localStorage.getItem("pdf-report-footer")) || "Confidential · Security scan history",
  );

  const load = async () => {
    setLoading(true);
    const [{ data: f }, { data: s }, { data: r }, { data: cs }, { data: au }, { data: ac }, { data: df }] =
      await Promise.all([
        supabase
          .from("security_scan_findings")
          .select("*")
          .order("status", { ascending: true })
          .order("last_seen_at", { ascending: false })
          .limit(500),
        supabase
          .from("security_scan_snapshots")
          .select("id, source, git_sha, finding_count, new_count, created_at, findings, counts_by_id")
          .order("created_at", { ascending: false })
          .limit(50),
        supabase
          .from("security_rescan_requests")
          .select("id, source, reason, status, latency_ms, created_at")
          .order("created_at", { ascending: false })
          .limit(100),
        supabase
          .from("ci_shared_secrets")
          .select("key, hint, rotated_at, rotated_by")
          .eq("key", "security_rescan_ci_secret")
          .maybeSingle(),
        supabase
          .from("admin_audit_logs")
          .select("id, user_id, action, target_table, target_id, details, created_at")
          .in("action", ["security.rescan.trigger", "ci_secret.rotate", "security.alert.resend"])
          .order("created_at", { ascending: false })
          .limit(200),
        supabase
          .from("security_alert_settings")
          .select("slack_webhook_url, notify_emails, notify_on_new_findings, notify_web_push, enabled")
          .eq("id", true)
          .maybeSingle(),
        supabase
          .from("security_alert_delivery_failures")
          .select("*")
          .order("created_at", { ascending: false })
          .limit(50),
      ]);
    setFindings((f as Finding[]) ?? []);
    setSnapshots((s as Snapshot[]) ?? []);
    setRescans((r as Rescan[]) ?? []);
    setCiSecret((cs as CiSecret) ?? null);
    setAudit((au as AuditRow[]) ?? []);
    if (ac) setAlertCfg(ac as AlertSettings);
    setFailures((df as DeliveryFailure[]) ?? []);
    setLoading(false);
  };

  const saveAlertCfg = async () => {
    setSavingCfg(true);
    const { error } = await supabase
      .from("security_alert_settings")
      .update({
        slack_webhook_url: alertCfg.slack_webhook_url?.trim() || null,
        notify_emails: alertCfg.notify_emails,
        notify_on_new_findings: alertCfg.notify_on_new_findings,
        notify_web_push: alertCfg.notify_web_push,
        enabled: alertCfg.enabled,
        updated_at: new Date().toISOString(),
      })
      .eq("id", true);
    setSavingCfg(false);
    if (error) return toast.error(error.message);
    toast.success("Notification settings saved");
  };

  const rotateCiSecret = async () => {
    if (!confirm("Rotate the CI shared secret? The existing value will stop working immediately.")) return;
    setRotating(true);
    setRotatedPlaintext(null);
    const { data: { session } } = await supabase.auth.getSession();
    const { data, error } = await supabase.functions.invoke("rotate-ci-secret", {
      body: { key: "security_rescan_ci_secret" },
      headers: session ? { Authorization: `Bearer ${session.access_token}` } : undefined,
    });
    setRotating(false);
    if (error) return toast.error(error.message);
    const secret = (data as { secret?: string })?.secret ?? null;
    setRotatedPlaintext(secret);
    toast.success("Secret rotated — copy the value into GitHub Secrets");
    load();
  };

  useEffect(() => {
    load();
  }, []);

  // Realtime: subscribe to timeline-relevant tables and refresh on any change.
  const loadRef = useRef(load);
  loadRef.current = load;
  const activeRun = rescans.some((r) => r.status === "pending" || r.status === "running");
  useEffect(() => {
    const channel = supabase
      .channel(`admin-security-timeline:${Math.random().toString(36).slice(2)}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "security_rescan_requests" }, () => loadRef.current())
      .on("postgres_changes", { event: "*", schema: "public", table: "security_scan_snapshots" }, () => loadRef.current())
      .on("postgres_changes", { event: "*", schema: "public", table: "security_alert_delivery_failures" }, () => loadRef.current())
      .on("postgres_changes", { event: "*", schema: "public", table: "admin_audit_logs" }, () => loadRef.current())
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return findings.filter((f) => {
      if (filter !== "all" && f.status !== filter) return false;
      if (!q) return true;
      return (
        f.internal_id.toLowerCase().includes(q) ||
        (f.name ?? "").toLowerCase().includes(q) ||
        (f.description ?? "").toLowerCase().includes(q)
      );
    });
  }, [findings, filter, query]);

  const ignoreFinding = async (f: Finding, reason: string) => {
    if (!reason.trim()) return toast.error("Add a reason first");
    const {
      data: { user },
    } = await supabase.auth.getUser();
    const { error } = await supabase
      .from("security_scan_findings")
      .update({
        status: "ignored",
        ignore_reason: reason.trim(),
        decided_by: user?.id ?? null,
        decided_at: new Date().toISOString(),
      })
      .eq("id", f.id);
    if (error) return toast.error(error.message);
    toast.success("Finding marked ignored");
    load();
  };

  const reopenFinding = async (f: Finding) => {
    const { error } = await supabase
      .from("security_scan_findings")
      .update({ status: "open", ignore_reason: null, decided_at: new Date().toISOString() })
      .eq("id", f.id);
    if (error) return toast.error(error.message);
    load();
  };

  const triggerRescan = async () => {
    setTriggering(true);
    const { data: { session } } = await supabase.auth.getSession();
    const { error } = await supabase.functions.invoke("security-rescan", {
      body: { source: "manual", reason: "Admin UI rescan", findings: [] },
      headers: session ? { Authorization: `Bearer ${session.access_token}` } : undefined,
    });
    setTriggering(false);
    if (error) return toast.error(error.message);
    toast.success("Rescan recorded");
    load();
  };

  const downloadMatrix = async (format: "csv" | "md" | "json") => {
    setDownloading(format);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const url = `${(import.meta as any).env?.VITE_SUPABASE_URL ?? ""}/functions/v1/role-matrix-export?format=${format}`;
      const resp = await fetch(url, {
        headers: {
          Authorization: `Bearer ${session?.access_token ?? ""}`,
          apikey: (import.meta as any).env?.VITE_SUPABASE_PUBLISHABLE_KEY ?? "",
        },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const a = document.createElement("a");
      const objUrl = URL.createObjectURL(blob);
      a.href = objUrl;
      a.download = `role-access-matrix.${format === "md" ? "md" : format}`;
      a.click();
      URL.revokeObjectURL(objUrl);
      toast.success(`Downloaded ${format.toUpperCase()}`);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Download failed");
    } finally {
      setDownloading(null);
    }
  };

  // Findings introduced by the most recent snapshot (not present in the previous one).
  const newIds = useMemo(() => {
    const latest = snapshots[0];
    if (!latest?.findings) return new Set<string>();
    const prev = new Set((snapshots[1]?.findings ?? []).map((f) => f.internal_id));
    return new Set(latest.findings.map((f) => f.internal_id).filter((id) => !prev.has(id)));
  }, [snapshots]);

  const downloadSecurityReport = () => {
    const doc = new jsPDF({ unit: "pt", format: "letter" });
    const margin = 48;
    const width = doc.internal.pageSize.getWidth();
    let y = margin;
    const latest = snapshots[0];

    const footer = () => {
      doc.setFontSize(8);
      doc.setTextColor(130);
      doc.text(reportFooter, margin, doc.internal.pageSize.getHeight() - 24);
      doc.setTextColor(0);
    };
    const nextPage = () => {
      footer();
      doc.addPage();
      y = margin;
    };
    const line = (text: string, size = 10, gap = 14) => {
      doc.setFontSize(size);
      for (const part of doc.splitTextToSize(text, width - margin * 2)) {
        if (y > doc.internal.pageSize.getHeight() - 60) nextPage();
        doc.text(part, margin, y);
        y += gap;
      }
    };

    doc.setFontSize(16);
    doc.text(`${productName} — security report`, margin, y);
    y += 20;
    line(`Generated ${new Date().toLocaleString()}`, 9, 12);
    line(
      latest
        ? `Latest scan ${new Date(latest.created_at).toLocaleString()} · source ${latest.source} · ${latest.finding_count} finding(s) · ${latest.new_count} new`
        : "No scan snapshots recorded yet.",
      9,
      18,
    );

    line(`Summary: ${counts.open} open · ${counts.fixed} fixed · ${counts.ignored} ignored`, 11, 20);

    const section = (title: string, rows: Finding[]) => {
      line(title, 12, 18);
      if (rows.length === 0) {
        line("None.", 9, 16);
        return;
      }
      for (const f of rows) {
        line(`• ${f.internal_id} [${f.level ?? "warn"}] — ${f.name ?? ""}`, 9, 12);
        if (f.status === "fixed") {
          line(
            `   fix confirmed by ${f.decided_by ? f.decided_by.slice(0, 8) + "…" : "automated rescan"}${
              f.decided_at ? ` on ${new Date(f.decided_at).toLocaleString()}` : ""
            }`,
            8,
            12,
          );
        }
        if (f.status === "ignored" && f.ignore_reason) {
          line(`   accepted risk: ${f.ignore_reason}`, 8, 12);
        }
      }
      y += 6;
    };

    section(
      "Open findings",
      findings.filter((f) => f.status === "open"),
    );
    section(
      "Fixes applied",
      findings.filter((f) => f.status === "fixed"),
    );
    section(
      "Accepted / ignored",
      findings.filter((f) => f.status === "ignored"),
    );

    footer();
    doc.save(`security-report-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.pdf`);
    toast.success("Security report PDF downloaded");
  };


  const counts = useMemo(() => {
    const c = { open: 0, ignored: 0, fixed: 0 };
    for (const f of findings) c[f.status]++;
    return c;
  }, [findings]);

  return (
    <div className="container mx-auto max-w-7xl px-4 py-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold flex items-center gap-2">
            <Shield className="size-6 text-primary" />
            Security scan findings
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Track scanner results across snapshots. Ignore non-actionable notes with a
            documented reason (e.g. <code className="font-mono text-xs">trends_no_insert_owner_check_ok</code>).
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={triggerRescan} disabled={triggering} size="sm">
            {triggering ? <Loader2 className="size-4 animate-spin mr-1" /> : <RefreshCw className="size-4 mr-1" />}
            Run security scan
          </Button>
          <Button size="sm" variant="outline" onClick={downloadSecurityReport}>
            <FileText className="size-4 mr-1" />
            Security report PDF
          </Button>
          <Button size="sm" variant="outline" asChild>
            <Link to="/admin/security-history">
              <History className="size-4 mr-1" />
              History
            </Link>
          </Button>
          <Button variant="outline" size="sm" asChild>
            <Link to="/admin">
              <ChevronLeft className="size-4 mr-1" />
              Back to Admin
            </Link>
          </Button>
        </div>

      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Open" value={counts.open} tone="alert" />
        <StatCard label="Ignored" value={counts.ignored} />
        <StatCard label="Fixed" value={counts.fixed} />
        <StatCard label="Snapshots" value={snapshots.length} />
      </div>

      <SecurityScanJobPanel onComplete={load} />

      <SecurityScanComparison snapshots={snapshots} />



      {(() => {
        const latestSnap = snapshots[0];
        const latestRescan = rescans[0];
        return (
          <div className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-center justify-between gap-3 mb-3">
              <h2 className="font-display text-lg font-bold">Latest security rescan</h2>
              <Button onClick={triggerRescan} disabled={triggering} size="sm" variant="outline">
                {triggering ? <Loader2 className="size-4 animate-spin mr-1" /> : <RefreshCw className="size-4 mr-1" />}
                Run security scan
              </Button>
            </div>
            {newIds.size > 0 && (
              <div role="status" className="rounded-lg border border-destructive/40 bg-destructive/5 p-3 mb-3">
                <div className="text-sm font-semibold text-destructive">
                  {newIds.size} new finding{newIds.size === 1 ? "" : "s"} in the latest scan
                </div>
                <div className="font-mono text-[10px] text-muted-foreground mt-1 break-all">
                  {[...newIds].join(", ")}
                </div>
              </div>
            )}

            {!latestSnap && !latestRescan ? (
              <p className="text-sm text-muted-foreground">No rescans recorded yet.</p>
            ) : (
              <div className="grid sm:grid-cols-3 gap-3 text-sm">
                <div>
                  <div className="font-mono text-[10px] uppercase text-muted-foreground">Status</div>
                  <div className="font-semibold">{latestRescan?.status ?? "—"}</div>
                  <div className="font-mono text-[10px] text-muted-foreground mt-0.5">
                    {latestRescan ? new Date(latestRescan.created_at).toLocaleString() : "—"}
                  </div>
                </div>
                <div>
                  <div className="font-mono text-[10px] uppercase text-muted-foreground">Findings</div>
                  <div className="font-semibold">{latestSnap?.finding_count ?? 0}</div>
                  {latestSnap && latestSnap.new_count > 0 && (
                    <div className="font-mono text-[10px] text-destructive mt-0.5">
                      +{latestSnap.new_count} new
                    </div>
                  )}
                </div>
                <div>
                  <div className="font-mono text-[10px] uppercase text-muted-foreground">Source</div>
                  <div className="font-semibold">{latestSnap?.source ?? latestRescan?.source ?? "—"}</div>
                  {latestRescan?.latency_ms != null && (
                    <div className="font-mono text-[10px] text-muted-foreground mt-0.5">
                      {latestRescan.latency_ms}ms
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      <div className="rounded-xl border border-border bg-card p-4">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h2 className="font-display text-lg font-bold">CI rescan shared secret</h2>
            <p className="text-xs text-muted-foreground mt-1">
              Sent by GitHub Actions as <code className="font-mono">x-ci-secret</code> to the
              <code className="font-mono"> security-rescan</code> function on every workflow run. Rotating
              here takes effect on the backend immediately; paste the new value into your GitHub repo
              secret <code className="font-mono">SECURITY_RESCAN_CI_SECRET</code>.
            </p>
          </div>
          <Button onClick={rotateCiSecret} disabled={rotating} size="sm" variant="outline">
            {rotating ? <Loader2 className="size-4 animate-spin mr-1" /> : <RefreshCw className="size-4 mr-1" />}
            Rotate secret
          </Button>
        </div>
        <div className="grid sm:grid-cols-3 gap-3 text-sm">
          <div>
            <div className="font-mono text-[10px] uppercase text-muted-foreground">Current hint</div>
            <div className="font-mono">{ciSecret?.hint ?? "not set"}</div>
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase text-muted-foreground">Rotated at</div>
            <div className="font-mono text-xs">
              {ciSecret?.rotated_at ? new Date(ciSecret.rotated_at).toLocaleString() : "—"}
            </div>
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase text-muted-foreground">Storage</div>
            <div className="font-mono text-xs">SHA-256 hash only</div>
          </div>
        </div>
        {rotatedPlaintext && (
          <div className="mt-3 rounded-md border border-primary/40 bg-primary/5 p-3">
            <div className="font-mono text-[10px] uppercase text-primary mb-1">
              New secret (shown once — copy now)
            </div>
            <div className="font-mono text-xs break-all select-all">{rotatedPlaintext}</div>
            <Button
              size="sm"
              variant="ghost"
              className="mt-2"
              onClick={() => {
                navigator.clipboard.writeText(rotatedPlaintext);
                toast.success("Copied");
              }}
            >
              Copy to clipboard
            </Button>
          </div>
        )}
      </div>

      <div className="rounded-xl border border-border bg-card p-4 flex flex-wrap items-center gap-3">
        <Button onClick={triggerRescan} disabled={triggering} size="sm">
          {triggering ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
          Record rescan
        </Button>
        <div className="flex-1" />
        <span className="font-mono text-[10px] uppercase text-muted-foreground">Role access matrix</span>
        <Button size="sm" variant="outline" onClick={() => downloadMatrix("csv")} disabled={!!downloading}>
          {downloading === "csv" ? <Loader2 className="size-4 animate-spin" /> : <Download className="size-4" />} CSV
        </Button>
        <Button size="sm" variant="outline" onClick={() => downloadMatrix("md")} disabled={!!downloading}>
          {downloading === "md" ? <Loader2 className="size-4 animate-spin" /> : <Download className="size-4" />} Markdown
        </Button>
        <Button size="sm" variant="outline" onClick={() => downloadMatrix("json")} disabled={!!downloading}>
          {downloading === "json" ? <Loader2 className="size-4 animate-spin" /> : <Download className="size-4" />} JSON
        </Button>
      </div>

      <div className="rounded-xl border border-border bg-card p-4">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex gap-2">
            {STATUS_FILTERS.map((s) => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                className={`font-mono text-xs uppercase px-3 py-1.5 rounded border transition ${
                  filter === s
                    ? "border-primary text-primary bg-primary/5"
                    : "border-border text-muted-foreground hover:text-foreground"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter by id / name / description"
            className="h-8 w-72 font-mono text-xs"
          />
        </div>

        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground">No findings match this view.</p>
        ) : (
          <div className="space-y-3">
            {filtered.map((f) => (
              <FindingRow
                key={f.id}
                finding={f}
                isNew={newIds.has(f.internal_id)}
                onIgnore={(reason) => ignoreFinding(f, reason)}
                onReopen={() => reopenFinding(f)}

              />
            ))}
          </div>
        )}
      </div>

      <SecurityScheduleSettingsPanel />


      <div className="rounded-xl border border-border bg-card p-4">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h2 className="font-display text-lg font-bold">Admin alert notifications</h2>
            <p className="text-xs text-muted-foreground mt-1">
              When a rescan reports new findings (which would block a merge), post to Slack and notify admins.
            </p>
          </div>
          <Button onClick={saveAlertCfg} size="sm" disabled={savingCfg}>
            {savingCfg && <Loader2 className="size-4 animate-spin mr-1" />}
            Save
          </Button>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="text-xs space-y-1">
            <span className="font-mono text-[10px] uppercase text-muted-foreground">Slack webhook URL</span>
            <Input
              type="url"
              value={alertCfg.slack_webhook_url ?? ""}
              onChange={(e) => setAlertCfg({ ...alertCfg, slack_webhook_url: e.target.value })}
              placeholder="https://hooks.slack.com/services/…"
              className="font-mono text-xs"
            />
          </label>
          <label className="text-xs space-y-1">
            <span className="font-mono text-[10px] uppercase text-muted-foreground">Admin emails (comma-separated)</span>
            <Input
              value={alertCfg.notify_emails.join(", ")}
              onChange={(e) =>
                setAlertCfg({
                  ...alertCfg,
                  notify_emails: e.target.value
                    .split(",")
                    .map((s) => s.trim())
                    .filter(Boolean),
                })
              }
              placeholder="[email protected], [email protected]"
              className="font-mono text-xs"
            />
          </label>
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={alertCfg.enabled}
              onChange={(e) => setAlertCfg({ ...alertCfg, enabled: e.target.checked })}
            />
            Notifications enabled
          </label>
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={alertCfg.notify_on_new_findings}
              onChange={(e) => setAlertCfg({ ...alertCfg, notify_on_new_findings: e.target.checked })}
            />
            Email admins on new / changed findings
          </label>
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={alertCfg.notify_web_push}
              onChange={async (e) => {
                const on = e.target.checked;
                if (on && typeof Notification !== "undefined" && Notification.permission !== "granted") {
                  const perm = await Notification.requestPermission();
                  if (perm !== "granted") {
                    toast.error("Browser notifications were blocked");
                    return;
                  }
                }
                setAlertCfg({ ...alertCfg, notify_web_push: on });
              }}
            />
            Also send web push alerts to this browser
          </label>
        </div>
      </div>

      {(() => {
        // Build timeline entries from audit + rescan_requests, then apply filters + pagination.
        type Entry = {
          key: string;
          when: string;
          kind: "rotate" | "trigger" | "request";
          runType: "manual" | "ci" | "migration" | "system";
          outcome: "pass" | "block" | "info";
          title: string;
          meta: string;
          detail?: string;
          durationMs?: number | null;
          newCount?: number;
          findingCount?: number;
          snapshotId?: string | null;
        };
        const all: Entry[] = [];
        for (const a of audit) {
          const d = (a.details ?? {}) as Record<string, unknown>;
          if (a.action === "ci_secret.rotate") {
            all.push({
              key: `a-${a.id}`,
              when: a.created_at,
              kind: "rotate",
              runType: "manual",
              outcome: "info",
              title: "CI shared secret rotated",
              meta: `hint ${String(d.hint ?? "—")}`,
              detail: `by ${a.user_id.slice(0, 8)}…`,
            });
          } else if (a.action === "security.rescan.trigger") {
            const newCount = Number(d.new_count ?? 0);
            const src = String(d.source ?? "manual");
            all.push({
              key: `a-${a.id}`,
              when: a.created_at,
              kind: "trigger",
              runType: src === "ci" ? "ci" : src === "migration" ? "migration" : "manual",
              outcome: newCount > 0 ? "block" : "pass",
              title: "Manual rescan triggered",
              meta: `${d.finding_count ?? 0} findings · ${d.latency_ms ?? "?"}ms${newCount > 0 ? ` · +${newCount} new` : ""}`,
              detail: `by ${a.user_id.slice(0, 8)}… · source ${src}`,
              durationMs: (d.latency_ms as number) ?? null,
              newCount,
              findingCount: Number(d.finding_count ?? 0),
              snapshotId: (a.target_id as string) ?? null,
            });
          }
        }
        for (const r of rescans) {
          all.push({
            key: `r-${r.id}`,
            when: r.created_at,
            kind: "request",
            runType: r.source === "ci" ? "ci" : r.source === "migration" ? "migration" : r.source === "admin" ? "manual" : "system",
            outcome: r.status === "ok" ? "pass" : r.status === "blocked" ? "block" : "info",
            title: `Rescan request (${r.status})`,
            meta: `${r.source}${r.latency_ms != null ? ` · ${r.latency_ms}ms` : ""}`,
            detail: r.reason ?? undefined,
            durationMs: r.latency_ms,
          });
        }
        all.sort((a, b) => (a.when < b.when ? 1 : -1));

        const fromTs = tlFrom ? new Date(tlFrom).getTime() : null;
        const toTs = tlTo ? new Date(tlTo).getTime() : null;
        const filteredEntries = all.filter((e) => {
          if (tlStatus !== "all" && e.outcome !== tlStatus) return false;
          if (tlKind !== "all" && e.runType !== tlKind) return false;
          const t = new Date(e.when).getTime();
          if (fromTs && t < fromTs) return false;
          if (toTs && t > toTs) return false;
          return true;
        });

        const perPage = 15;
        const pageCount = Math.max(1, Math.ceil(filteredEntries.length / perPage));
        const safePage = Math.min(tlPage, pageCount - 1);
        const paged = filteredEntries.slice(safePage * perPage, safePage * perPage + perPage);

        const findSnap = (id?: string | null): Snapshot | null => {
          if (!id) return null;
          return snapshots.find((s) => s.id === id) ?? null;
        };

        const downloadReport = () => {
          const cols = ["timestamp", "kind", "run_type", "outcome", "title", "finding_count", "new_count", "duration_ms", "detail"];
          const rows = filteredEntries.map((e) => [
            e.when,
            e.kind,
            e.runType,
            e.outcome,
            e.title,
            e.findingCount ?? "",
            e.newCount ?? "",
            e.durationMs ?? "",
            (e.detail ?? "").replace(/\s+/g, " ").trim(),
          ]);
          const esc = (v: unknown) => {
            const s = v == null ? "" : String(v);
            return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
          };
          const csv = [cols.join(","), ...rows.map((r) => r.map(esc).join(","))].join("\n");
          const blob = new Blob([csv], { type: "text/csv" });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `rescan-history-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.csv`;
          a.click();
          URL.revokeObjectURL(url);
          toast.success("Rescan history CSV downloaded");
        };

        const downloadPdf = () => {
          const doc = new jsPDF({ unit: "pt", format: "letter" });
          const margin = 40;
          const width = doc.internal.pageSize.getWidth();
          const height = doc.internal.pageSize.getHeight();
          const generatedAt = new Date().toLocaleString();
          const filterLine = `Filters — status: ${tlStatus} · type: ${tlKind} · from: ${tlFrom || "—"} · to: ${tlTo || "—"}`;

          const drawHeaderFooter = () => {
            // Header
            doc.setFont("helvetica", "bold");
            doc.setFontSize(11);
            doc.setTextColor(0);
            doc.text(productName || "Security report", margin, 28);
            doc.setFont("helvetica", "normal");
            doc.setFontSize(8);
            doc.setTextColor(120);
            doc.text(`Generated ${generatedAt}`, width - margin, 20, { align: "right" });
            doc.text(filterLine, width - margin, 32, { align: "right" });
            doc.setDrawColor(220);
            doc.line(margin, 40, width - margin, 40);
            // Footer
            doc.setFontSize(8);
            doc.setTextColor(120);
            if (reportFooter) doc.text(reportFooter, margin, height - 20);
            doc.text(
              `Page ${doc.getCurrentPageInfo().pageNumber}`,
              width - margin,
              height - 20,
              { align: "right" },
            );
            doc.setTextColor(0);
          };

          let y = 60;
          drawHeaderFooter();
          doc.setFontSize(16);
          doc.setFont("helvetica", "bold");
          doc.text("Security rescan history", margin, y);
          y += 18;
          doc.setFont("helvetica", "normal");
          doc.setFontSize(9);
          doc.setTextColor(90);
          doc.text(`${filteredEntries.length} entries`, margin, y);
          y += 16;
          doc.setTextColor(0);

          for (const e of filteredEntries) {
            if (y > height - 60) { doc.addPage(); y = 60; drawHeaderFooter(); }
            doc.setFontSize(10);
            doc.setFont("helvetica", "bold");
            doc.text(`${e.title}  [${e.outcome.toUpperCase()} · ${e.runType}]`, margin, y);
            y += 12;
            doc.setFont("helvetica", "normal");
            doc.setFontSize(8);
            doc.setTextColor(90);
            doc.text(new Date(e.when).toLocaleString(), margin, y);
            y += 10;
            const meta = `${e.meta}${e.durationMs != null ? ` · duration ${e.durationMs}ms` : ""}`;
            for (const line of doc.splitTextToSize(meta, width - margin * 2)) { doc.text(line, margin, y); y += 10; }
            if (e.detail) {
              for (const line of doc.splitTextToSize(e.detail, width - margin * 2)) { doc.text(line, margin, y); y += 10; }
            }
            const snap = findSnap(e.snapshotId);
            if (snap) {
              const diffLine = `Diff summary — findings: ${snap.finding_count} · new: ${snap.new_count}`;
              doc.setTextColor(120, 40, 40);
              doc.text(diffLine, margin, y);
              y += 10;
              doc.setTextColor(90);
            }
            doc.setDrawColor(220);
            doc.line(margin, y, width - margin, y);
            y += 10;
            doc.setTextColor(0);
          }
          doc.save(`rescan-history-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.pdf`);
          toast.success("Rescan history PDF downloaded");
        };

        return (
          <div className="rounded-xl border border-border bg-card p-4">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <h2 className="font-display text-lg font-bold">
                Rescan history timeline
                {activeRun && (
                  <span className="ml-2 font-mono text-[10px] uppercase text-primary animate-pulse">● live</span>
                )}
              </h2>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={downloadReport} disabled={filteredEntries.length === 0}>
                  <Download className="size-4 mr-1" /> CSV
                </Button>
                <Button size="sm" variant="outline" onClick={downloadPdf} disabled={filteredEntries.length === 0}>
                  <Download className="size-4 mr-1" /> PDF
                </Button>
              </div>
            </div>

            <div className="grid sm:grid-cols-4 gap-2 mb-4 text-xs">
              <label className="space-y-1">
                <span className="font-mono text-[10px] uppercase text-muted-foreground">Status</span>
                <Select value={tlStatus} onValueChange={(v) => { setTlStatus(v as typeof tlStatus); setTlPage(0); }}>
                  <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="pass">Pass</SelectItem>
                    <SelectItem value="block">Block</SelectItem>
                  </SelectContent>
                </Select>
              </label>
              <label className="space-y-1">
                <span className="font-mono text-[10px] uppercase text-muted-foreground">Run type</span>
                <Select value={tlKind} onValueChange={(v) => { setTlKind(v as typeof tlKind); setTlPage(0); }}>
                  <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="manual">Manual</SelectItem>
                    <SelectItem value="ci">CI</SelectItem>
                    <SelectItem value="migration">Migration</SelectItem>
                  </SelectContent>
                </Select>
              </label>
              <label className="space-y-1">
                <span className="font-mono text-[10px] uppercase text-muted-foreground">From</span>
                <Input type="datetime-local" value={tlFrom} onChange={(e) => { setTlFrom(e.target.value); setTlPage(0); }} className="h-8" />
              </label>
              <label className="space-y-1">
                <span className="font-mono text-[10px] uppercase text-muted-foreground">To</span>
                <Input type="datetime-local" value={tlTo} onChange={(e) => { setTlTo(e.target.value); setTlPage(0); }} className="h-8" />
              </label>
            </div>

            {filteredEntries.length === 0 ? (
              <p className="text-sm text-muted-foreground">No entries match these filters.</p>
            ) : (
              <>
                <ol className="relative border-l border-border ml-2 space-y-4">
                  {paged.map((e) => {
                    const clickable = e.kind === "trigger" && e.snapshotId;
                    return (
                      <li key={e.key} className="ml-4">
                        <span
                          className={`absolute -left-1.5 mt-1.5 size-3 rounded-full border-2 border-background ${
                            e.outcome === "block"
                              ? "bg-destructive"
                              : e.outcome === "pass"
                              ? "bg-primary"
                              : e.kind === "rotate"
                              ? "bg-primary"
                              : "bg-muted-foreground"
                          }`}
                        />
                        <div className="flex items-center justify-between gap-2">
                          <div className="font-semibold text-sm flex items-center gap-2">
                            {e.title}
                            <span className={`font-mono text-[9px] uppercase px-1.5 rounded border ${
                              e.outcome === "block" ? "border-destructive/40 text-destructive" :
                              e.outcome === "pass" ? "border-primary/40 text-primary" :
                              "border-border text-muted-foreground"
                            }`}>{e.outcome}</span>
                            <span className="font-mono text-[9px] uppercase text-muted-foreground">{e.runType}</span>
                          </div>
                          <div className="font-mono text-[10px] text-muted-foreground">
                            {new Date(e.when).toLocaleString()}
                          </div>
                        </div>
                        <div className="font-mono text-[10px] text-muted-foreground">{e.meta}</div>
                        {e.detail && <div className="text-xs text-muted-foreground">{e.detail}</div>}
                        {clickable && (
                          <button
                            onClick={() => setDiffSnap(findSnap(e.snapshotId))}
                            className="mt-1 text-[11px] text-primary hover:underline font-mono"
                          >
                            View diff →
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ol>
                <div className="flex items-center justify-between mt-4 text-xs">
                  <div className="font-mono text-muted-foreground">
                    {filteredEntries.length} entries · page {safePage + 1} / {pageCount}
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" disabled={safePage === 0} onClick={() => setTlPage(safePage - 1)}>Prev</Button>
                    <Button size="sm" variant="outline" disabled={safePage >= pageCount - 1} onClick={() => setTlPage(safePage + 1)}>Next</Button>
                  </div>
                </div>
              </>
            )}
          </div>
        );
      })()}

      <div className="rounded-xl border border-border bg-card p-4">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h2 className="font-display text-lg font-bold">PDF report branding</h2>
            <p className="text-xs text-muted-foreground mt-1">
              Shown in the header and footer of downloaded rescan history PDFs, alongside the generation
              timestamp and active filters. Saved locally to this browser.
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              localStorage.setItem("pdf-report-product-name", productName);
              localStorage.setItem("pdf-report-footer", reportFooter);
              toast.success("PDF branding saved");
            }}
          >
            Save
          </Button>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="text-xs space-y-1">
            <span className="font-mono text-[10px] uppercase text-muted-foreground">Product name (header)</span>
            <Input value={productName} onChange={(e) => setProductName(e.target.value)} className="font-mono text-xs" />
          </label>
          <label className="text-xs space-y-1">
            <span className="font-mono text-[10px] uppercase text-muted-foreground">Footer line</span>
            <Input value={reportFooter} onChange={(e) => setReportFooter(e.target.value)} className="font-mono text-xs" />
          </label>
        </div>
      </div>

      <AdminActivityAuditPanel rows={audit} />

      <DeliveryFailuresPanel failures={failures} onReload={load} />


      {diffSnap && (
        <DiffModal
          snapshot={diffSnap}
          previous={snapshots.find((s) => new Date(s.created_at) < new Date(diffSnap.created_at)) ?? null}
          onClose={() => setDiffSnap(null)}
        />
      )}

      <div className="grid md:grid-cols-2 gap-4">
        <div className="rounded-xl border border-border bg-card p-4">
          <h2 className="font-display text-lg font-bold mb-3">Recent snapshots</h2>
          {snapshots.length === 0 ? (
            <p className="text-sm text-muted-foreground">No snapshots yet.</p>
          ) : (
            <ul className="divide-y divide-border text-sm">
              {snapshots.map((s) => (
                <li key={s.id} className="py-2 flex items-center justify-between gap-2">
                  <div>
                    <span className="font-mono text-[10px] uppercase text-muted-foreground mr-2">
                      {s.source}
                    </span>
                    {s.git_sha && (
                      <span className="font-mono text-[10px] text-muted-foreground">
                        {s.git_sha.slice(0, 7)}
                      </span>
                    )}
                    <div className="font-mono text-[10px] text-muted-foreground">
                      {new Date(s.created_at).toLocaleString()}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-xs">{s.finding_count} findings</div>
                    {s.new_count > 0 && (
                      <div className="font-mono text-[10px] text-destructive">
                        +{s.new_count} new
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="rounded-xl border border-border bg-card p-4">
          <h2 className="font-display text-lg font-bold mb-3">Rescan requests</h2>
          {rescans.length === 0 ? (
            <p className="text-sm text-muted-foreground">No rescans yet.</p>
          ) : (
            <ul className="divide-y divide-border text-sm">
              {rescans.map((r) => (
                <li key={r.id} className="py-2">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-[10px] uppercase text-muted-foreground">
                      {r.source} · {r.status}
                    </span>
                    <span className="font-mono text-[10px] text-muted-foreground">
                      {new Date(r.created_at).toLocaleString()}
                    </span>
                  </div>
                  {r.reason && <div className="text-xs text-muted-foreground">{r.reason}</div>}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, tone }: { label: string; value: number; tone?: "alert" }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="font-mono text-[10px] uppercase text-muted-foreground">{label}</div>
      <div
        className={`font-display text-2xl font-bold ${
          tone === "alert" && value > 0 ? "text-destructive" : ""
        }`}
      >
        {value}
      </div>
    </div>
  );
}

function FindingRow({
  finding,
  isNew,
  onIgnore,
  onReopen,
}: {
  finding: Finding;
  isNew?: boolean;
  onIgnore: (reason: string) => void;
  onReopen: () => void;
}) {
  const [reason, setReason] = useState(finding.ignore_reason ?? "");
  const [expand, setExpand] = useState(false);
  const isIgnored = finding.status === "ignored";
  return (
    <div
      className={`rounded-lg border bg-secondary/20 p-3 ${
        isNew ? "border-destructive/60 ring-1 ring-destructive/30" : "border-border/60"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {isNew && (
              <span className="font-mono text-[10px] uppercase rounded px-1.5 py-0.5 bg-destructive text-destructive-foreground">
                new
              </span>
            )}

            <span
              className={`font-mono text-[10px] uppercase rounded px-1.5 py-0.5 border ${
                finding.status === "open"
                  ? "border-destructive/40 text-destructive"
                  : finding.status === "ignored"
                  ? "border-muted-foreground/40 text-muted-foreground"
                  : "border-primary/40 text-primary"
              }`}
            >
              {finding.status}
            </span>
            <span className="font-mono text-[10px] text-muted-foreground">
              {finding.scanner_name}
            </span>
            {finding.level && (
              <span className="font-mono text-[10px] text-muted-foreground uppercase">
                {finding.level}
              </span>
            )}
          </div>
          <div className="font-mono text-xs mt-1 break-all">{finding.internal_id}</div>
          {finding.name && <div className="text-sm mt-0.5">{finding.name}</div>}
          {isIgnored && finding.ignore_reason && (
            <div className="text-xs text-muted-foreground mt-1">
              <b>Why ignored:</b> {finding.ignore_reason}
            </div>
          )}
        </div>
        <Button size="sm" variant="ghost" onClick={() => setExpand((v) => !v)}>
          {expand ? "Hide" : "Manage"}
        </Button>
      </div>
      {expand && (
        <div className="mt-3 space-y-2">
          {finding.description && (
            <p className="text-xs text-muted-foreground">{finding.description}</p>
          )}
          {!isIgnored ? (
            <>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Why is this finding non-actionable here?"
                className="text-xs"
              />
              <Button size="sm" onClick={() => onIgnore(reason)} disabled={!reason.trim()}>
                Mark ignored
              </Button>
            </>
          ) : (
            <Button size="sm" variant="outline" onClick={onReopen}>
              Reopen
            </Button>
          )}
          <SecurityRemediationPanel findingId={finding.id} />
        </div>
      )}
    </div>
  );
}

function DeliveryFailuresPanel({ failures, onReload }: { failures: DeliveryFailure[]; onReload: () => void }) {
  const [resending, setResending] = useState<string | null>(null);
  const unresolved = failures.filter((f) => !f.resolved_at);
  const dueNow = unresolved.filter((f) => !f.next_attempt_at || new Date(f.next_attempt_at) <= new Date()).length;

  const markResolved = async (id: string) => {
    const { error } = await supabase
      .from("security_alert_delivery_failures")
      .update({ resolved_at: new Date().toISOString() })
      .eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Marked resolved");
    onReload();
  };

  const resend = async (f: DeliveryFailure) => {
    setResending(f.id);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const { data, error } = await supabase.functions.invoke("resend-security-alert", {
        body: { failure_id: f.id },
        headers: session ? { Authorization: `Bearer ${session.access_token}` } : undefined,
      });
      if (error) throw new Error(error.message);
      const res = data as { ok?: boolean; last_error?: string | null };
      if (res?.ok) toast.success(`Re-sent to ${f.target ?? f.channel}`);
      else toast.error(`Re-send failed: ${res?.last_error ?? "unknown error"}`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Re-send failed");
    } finally {
      setResending(null);
      onReload();
    }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <h2 className="font-display text-lg font-bold">Notification retry queue</h2>
          <p className="text-xs text-muted-foreground mt-1">
            Slack and email alerts are retried up to 3 times with exponential backoff. Persistent failures are
            queued here with the next-attempt time, the last error, and a manual re-send per recipient.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {dueNow > 0 && (
            <span className="font-mono text-[10px] uppercase px-2 py-1 rounded border border-primary/40 text-primary">
              {dueNow} due now
            </span>
          )}
          {unresolved.length > 0 && (
            <span className="font-mono text-[10px] uppercase px-2 py-1 rounded border border-destructive/40 text-destructive">
              {unresolved.length} queued
            </span>
          )}
        </div>
      </div>
      {failures.length === 0 ? (
        <p className="text-sm text-muted-foreground">Queue empty — all alerts delivered successfully.</p>
      ) : (
        <ul className="divide-y divide-border text-sm">
          {failures.map((f) => {
            const nextAt = f.next_attempt_at ? new Date(f.next_attempt_at) : null;
            const due = !nextAt || nextAt <= new Date();
            return (
              <li key={f.id} className="py-3">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2">
                    <span className={`font-mono text-[10px] uppercase px-1.5 rounded border ${
                      f.channel === "slack" ? "border-primary/40 text-primary" : "border-muted-foreground/40 text-muted-foreground"
                    }`}>{f.channel}</span>
                    <span className="font-mono text-xs break-all">{f.target ?? "—"}</span>
                    <span className="font-mono text-[10px] text-muted-foreground">
                      {f.attempts} attempts{f.last_status_code ? ` · HTTP ${f.last_status_code}` : ""}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {f.resolved_at ? (
                      <span className="font-mono text-[10px] text-primary">resolved</span>
                    ) : (
                      <>
                        <Button size="sm" variant="outline" disabled={resending === f.id} onClick={() => resend(f)}>
                          {resending === f.id ? <Loader2 className="size-3 animate-spin mr-1" /> : <RefreshCw className="size-3 mr-1" />}
                          Re-send
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => markResolved(f.id)}>Resolve</Button>
                      </>
                    )}
                  </div>
                </div>
                <div className="mt-1 grid sm:grid-cols-3 gap-1 font-mono text-[10px] text-muted-foreground">
                  <div>first failed: {new Date(f.created_at).toLocaleString()}</div>
                  <div>last attempt: {f.last_attempt_at ? new Date(f.last_attempt_at).toLocaleString() : "—"}</div>
                  <div className={!f.resolved_at && due ? "text-primary" : ""}>
                    next attempt: {f.resolved_at ? "—" : nextAt ? `${nextAt.toLocaleString()}${due ? " (due)" : ""}` : "manual only"}
                  </div>
                </div>
                {f.last_error && (
                  <div className="mt-1 text-xs text-destructive font-mono break-all">
                    {f.last_error}
                  </div>
                )}
                {(f.new_count != null || f.finding_count != null) && (
                  <div className="mt-1 font-mono text-[10px] text-muted-foreground">
                    snapshot: {f.finding_count ?? 0} findings · +{f.new_count ?? 0} new
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}


function DiffModal({
  snapshot,
  previous,
  onClose,
}: {
  snapshot: Snapshot;
  previous: Snapshot | null;
  onClose: () => void;
}) {
  const cur = snapshot.findings ?? [];
  const prev = previous?.findings ?? [];
  const curMap = new Map(cur.map((f) => [`${f.scanner_name}::${f.internal_id}`, f]));
  const prevMap = new Map(prev.map((f) => [`${f.scanner_name}::${f.internal_id}`, f]));

  const added = cur.filter((f) => !prevMap.has(`${f.scanner_name}::${f.internal_id}`));
  const removed = prev.filter((f) => !curMap.has(`${f.scanner_name}::${f.internal_id}`));
  const modified = cur.filter((f) => {
    const p = prevMap.get(`${f.scanner_name}::${f.internal_id}`);
    if (!p) return false;
    return (p.level ?? "") !== (f.level ?? "") || (p.name ?? "") !== (f.name ?? "") || (p.description ?? "") !== (f.description ?? "");
  });

  const [dQuery, setDQuery] = useState("");
  const [dSort, setDSort] = useState<"id-asc" | "id-desc" | "level" | "scanner">("id-asc");

  const applyFilters = (
    items: Array<{ internal_id: string; scanner_name: string; level?: string | null; name?: string | null; description?: string | null }>,
  ) => {
    const q = dQuery.trim().toLowerCase();
    const filtered = q
      ? items.filter(
          (f) =>
            f.internal_id.toLowerCase().includes(q) ||
            f.scanner_name.toLowerCase().includes(q) ||
            (f.name ?? "").toLowerCase().includes(q) ||
            (f.description ?? "").toLowerCase().includes(q) ||
            (f.level ?? "").toLowerCase().includes(q),
        )
      : items;
    const sorted = [...filtered];
    sorted.sort((a, b) => {
      if (dSort === "id-asc") return a.internal_id.localeCompare(b.internal_id);
      if (dSort === "id-desc") return b.internal_id.localeCompare(a.internal_id);
      if (dSort === "level") return (a.level ?? "").localeCompare(b.level ?? "");
      return a.scanner_name.localeCompare(b.scanner_name);
    });
    return sorted;
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card border border-border rounded-xl max-w-3xl w-full max-h-[85vh] overflow-auto p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <h3 className="font-display text-lg font-bold">Rescan diff</h3>
            <p className="text-xs text-muted-foreground mt-1 font-mono">
              {new Date(snapshot.created_at).toLocaleString()} · {snapshot.source}
              {snapshot.git_sha ? ` · ${snapshot.git_sha.slice(0, 7)}` : ""}
              {previous ? ` vs ${new Date(previous.created_at).toLocaleString()}` : " (no prior snapshot)"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" asChild>
              <Link to={`/admin/security-findings/diff/${snapshot.id}`} onClick={onClose}>
                Field-level drilldown →
              </Link>
            </Button>
            <Button size="sm" variant="ghost" onClick={onClose}>Close</Button>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3 mb-4 text-center">
          <div className="rounded border border-destructive/40 p-2">
            <div className="font-mono text-[10px] uppercase text-destructive">Added</div>
            <div className="font-display text-2xl font-bold text-destructive">{added.length}</div>
          </div>
          <div className="rounded border border-primary/40 p-2">
            <div className="font-mono text-[10px] uppercase text-primary">Removed</div>
            <div className="font-display text-2xl font-bold text-primary">{removed.length}</div>
          </div>
          <div className="rounded border border-border p-2">
            <div className="font-mono text-[10px] uppercase text-muted-foreground">Modified</div>
            <div className="font-display text-2xl font-bold">{modified.length}</div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mb-4">
          <Input
            value={dQuery}
            onChange={(e) => setDQuery(e.target.value)}
            placeholder="Search finding id / scanner / name / value"
            className="h-8 flex-1 min-w-[180px] font-mono text-xs"
          />
          <Select value={dSort} onValueChange={(v) => setDSort(v as typeof dSort)}>
            <SelectTrigger className="h-8 w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="id-asc">Sort: ID ↑</SelectItem>
              <SelectItem value="id-desc">Sort: ID ↓</SelectItem>
              <SelectItem value="level">Sort: level</SelectItem>
              <SelectItem value="scanner">Sort: scanner</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <DiffSection title="Added" tone="destructive" items={applyFilters(added)} />
        <DiffSection title="Removed" tone="primary" items={applyFilters(removed)} />
        <DiffSection title="Modified" tone="muted" items={applyFilters(modified)} />
      </div>
    </div>
  );
}

function DiffSection({
  title,
  tone,
  items,
}: {
  title: string;
  tone: "destructive" | "primary" | "muted";
  items: Array<{ internal_id: string; scanner_name: string; level?: string | null; name?: string | null; description?: string | null }>;
}) {
  if (!items.length) return null;
  const toneClass =
    tone === "destructive" ? "text-destructive" : tone === "primary" ? "text-primary" : "text-muted-foreground";
  return (
    <div className="mb-4">
      <h4 className={`font-display text-sm font-bold mb-2 ${toneClass}`}>{title} ({items.length})</h4>
      <ul className="space-y-1.5">
        {items.map((f, i) => (
          <li key={`${f.scanner_name}-${f.internal_id}-${i}`} className="text-xs rounded border border-border/60 p-2">
            <div className="font-mono break-all">{f.internal_id}</div>
            {f.name && <div className="text-muted-foreground mt-0.5">{f.name}</div>}
            <div className="font-mono text-[10px] text-muted-foreground mt-0.5">
              {f.scanner_name}{f.level ? ` · ${f.level}` : ""}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function AdminActivityAuditPanel({ rows }: { rows: AuditRow[] }) {
  const [q, setQ] = useState("");
  const label = (a: string) =>
    a === "security.rescan.trigger" ? "Trigger rescan" :
    a === "security.alert.resend" ? "Re-send notification" :
    a === "ci_secret.rotate" ? "Rotate CI secret" : a;
  const filtered = rows.filter((r) => {
    if (!q.trim()) return true;
    const needle = q.toLowerCase();
    return (
      r.action.toLowerCase().includes(needle) ||
      r.user_id.toLowerCase().includes(needle) ||
      (r.target_id ?? "").toLowerCase().includes(needle) ||
      JSON.stringify(r.details ?? {}).toLowerCase().includes(needle)
    );
  });
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <h2 className="font-display text-lg font-bold">Admin action audit</h2>
          <p className="text-xs text-muted-foreground mt-1">
            Every "Trigger rescan now," notification re-send, and CI secret rotation is recorded with the
            actor and timestamp.
          </p>
        </div>
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Filter by action / actor / target"
          className="h-8 w-64 font-mono text-xs"
        />
      </div>
      {filtered.length === 0 ? (
        <p className="text-sm text-muted-foreground">No matching admin actions.</p>
      ) : (
        <ul className="divide-y divide-border text-sm max-h-96 overflow-auto">
          {filtered.map((r) => (
            <li key={r.id} className="py-2 flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-semibold">{label(r.action)}</div>
                <div className="font-mono text-[10px] text-muted-foreground break-all">
                  actor {r.user_id.slice(0, 8)}…{r.target_id ? ` · target ${r.target_id.slice(0, 12)}…` : ""}
                </div>
                {r.details && Object.keys(r.details).length > 0 && (
                  <div className="font-mono text-[10px] text-muted-foreground mt-0.5 break-all">
                    {Object.entries(r.details).slice(0, 4).map(([k, v]) => `${k}: ${String(v)}`).join(" · ")}
                  </div>
                )}
              </div>
              <div className="font-mono text-[10px] text-muted-foreground whitespace-nowrap">
                {new Date(r.created_at).toLocaleString()}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
